import React from "react";
import ReCAPTCHA from 'react-google-recaptcha';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/material/Stack';

import LoadingButton from "@mui/lab/LoadingButton";
import EmailIcon from "@mui/icons-material/Email";
import backgroundImage from '../../../assets/images/Asset-3-100.jpg';

import { useManageEpayco } from "../hooks/useManageEpayco";

import { Dialog } from '../../../components/others/Dialog';
import { ActionButtons } from "../../../components/others/ActionButtons";

const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const bucket = process.env.REACT_BUCKET_PAGE;

export const LayoutEpayco = ({ token, subscriptionPlan }) => {
    const {
        form,
        captchaToken,
        captchaRef,
        sdkReady,
        errorInputs,
        otpSent,
        loading,
        handleInputChange,
        handleSubmit,
        onChange,
        onExpired,
        validateCode,
        handleCancel
    } = useManageEpayco(token, subscriptionPlan);

    if (!sdkReady) {
        return <p>Cargando módulo de pagos…</p>;
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                alignItems: "center",
                background: `url('${backgroundImage}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
                width: '100vw',
                p: { xs: 2, md: 3 },
                pr: { md: 12 },
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    maxWidth: 600,
                    width: "100%",
                    p: 4,
                    height: "auto",
                    maxHeight: "calc(100vh - 40px)",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0,0,0,0.2)",
                        borderRadius: "10px"
                    },
                }}
            >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Registro de datos de usuario y pago
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                    Completa los siguientes campos datos para continuar al pago.
                </Typography>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12 }}>
                        <TextField
                            fullWidth
                            required
                            name="company_name"
                            label="Razón social o nombre comercial (En caso de ser empresa)"
                            size="small"
                            type="text"
                            value={form.company_name}
                            error={Boolean(errorInputs.company_name?.isError)}
                            helperText={errorInputs.company_name?.messageError || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl
                            fullWidth
                            required
                            error={Boolean(errorInputs.tax_id?.isError)}
                            helperText={errorInputs.tax_id?.messageError || ''}
                            size="small"
                        >
                            <InputLabel>Tipo de documento</InputLabel>
                            <Select
                                name="tax_id"
                                label="Tipo de Tributo"
                                value={form.tax_id}
                                onChange={handleInputChange}
                            >
                                {/* CC, CE, NIT, PAS  */}
                                <MenuItem value="CC">Cédula de ciudadanía</MenuItem>
                                <MenuItem value="CE">Cédula de extranjería</MenuItem>
                                <MenuItem value="NIT">NIT</MenuItem>
                                <MenuItem value="PAS">Pasaporte</MenuItem>
                                {/* <MenuItem value="13">Cédula de ciudadanía</MenuItem>
                                <MenuItem value="22">Cédula de extranjería</MenuItem>
                                <MenuItem value="31">NIT</MenuItem>
                                <MenuItem value="41">Pasaporte</MenuItem> */}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            name="id_number"
                            label="Número de documento"
                            size="small"
                            type="number"
                            value={form.id_number}
                            error={Boolean(errorInputs.id_number?.isError)}
                            helperText={errorInputs.id_number?.messageError || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            name='full_username'
                            label="Nombre completo de usuario"
                            size="small"
                            value={form.full_username}
                            error={Boolean(errorInputs.full_username?.isError)}
                            helperText={errorInputs.full_username?.messageError || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            name='email'
                            label="Correo electrónico"
                            size="small"
                            value={form.email}
                            type="email"
                            error={Boolean(errorInputs.email?.isError)}
                            helperText={errorInputs.email?.messageError || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            name="phone"
                            label="Teléfono"
                            size="small"
                            type="number"
                            value={form.phone}
                            error={Boolean(errorInputs.phone?.isError)}
                            helperText={errorInputs.phone?.messageError || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            name="address"
                            label="Dirección"
                            size="small"
                            value={form.address}
                            error={Boolean(errorInputs.address?.isError)}
                            helperText={errorInputs.address?.messageError || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.habeasData}
                                    onChange={handleInputChange}
                                    name="habeasData"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    * Acepto el tratamiento de mis datos personales{" "}
                                    <Link
                                        href={`https://s3.us-east-1.amazonaws.com/${bucket}/resource/document/privacy-policy-2026-06-15.pdf`}
                                        target="_blank"
                                        rel="noopener"
                                        underline="hover">
                                        (leer documento)
                                    </Link>
                                </Typography>
                            }
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.terms_conditions}
                                    onChange={handleInputChange}
                                    name="terms_conditions"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    * Acepto los términos y condiciones de uso{" "}
                                    <Link
                                        href={`https://s3.us-east-1.amazonaws.com/${bucket}/resource/document/terms-and-conditions-2026-06-15.pdf`}
                                        target="_blank"
                                        rel="noopener"
                                        underline="hover">
                                        (leer documento)
                                    </Link>
                                </Typography>
                            }
                        />
                    </FormGroup>

                    <ReCAPTCHA
                        ref={captchaRef}
                        sitekey={siteKey}
                        onChange={onChange}
                        onExpired={onExpired}
                    />

                    <Grid size={{ xs: 12, sm: 12 }} sx={{ mt: 1 }}>
                        <LoadingButton
                            disabled={!(form.terms_conditions && form.habeasData && captchaToken) || otpSent}
                            onClick={handleSubmit}
                            variant="contained"
                            fullWidth
                            loading={loading}
                            loadingPosition="start"
                            startIcon={<EmailIcon />}
                            size="large"
                        >
                            Enviar código de confirmación
                        </LoadingButton>
                    </Grid>
                </Grid>
            </Paper>

            <Dialog
                openDialog={otpSent}
                title="Código de confirmación"
                styleDialog={{
                    width: '350px',
                }}
                content={
                    <Stack spacing={2} mt={1}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                required
                                name="otpVefication"
                                label="Codigo de verificación"
                                size="small"
                                value={form.otpVefication}
                                error={Boolean(errorInputs.otpVefication?.isError)}
                                helperText={errorInputs.otpVefication?.messageError || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                    </Stack>
                }
                actions={
                    <ActionButtons
                        onCancel={handleCancel}
                        onSubmit={validateCode}
                        submitLabel="Continuar"
                    />
                }
                styleActions={{ margin: '10px 16px' }}
            />
        </Box >
    );
}
